import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Table, Tag, Button, Space, Typography, Empty, Spin, message } from 'antd';
import { EyeOutlined, ClockCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '@/firebase';
import dayjs from 'dayjs';

const { Text } = Typography;

interface Invitation {
  id: string;
  userEmail: string;
  userDisplayName: string;
  role: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  invitedAt: Date;
  expiresAt: Date;
  inviter: {
    displayName: string;
  };
  workspace: {
    name: string;
  };
}

interface InvitationsTabProps {
  userId: string;
  userType: 'resident' | 'employee';
}

const InvitationsTab: React.FC<InvitationsTabProps> = ({ userId }) => {
  const { t } = useTranslation(['common', 'invitations']);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInvitations();
  }, [userId]);

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const invitationsRef = collection(db, 'invitations');
      const q = query(
        invitationsRef,
        where('userId', '==', userId),
        where('status', '==', 'pending'),
        orderBy('invitedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const invitationsData: Invitation[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        invitationsData.push({
          id: doc.id,
          userEmail: data.userEmail,
          userDisplayName: data.userDisplayName,
          role: data.role,
          status: data.status,
          invitedAt: data.invitedAt?.toDate() || new Date(),
          expiresAt: data.expiresAt?.toDate() || new Date(),
          inviter: data.inviter,
          workspace: data.workspace
        });
      });
      
      setInvitations(invitationsData);
    } catch (error) {
      console.error('Error fetching invitations:', error);
      message.error(t('common:fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusTag = (status: string, expiresAt: Date) => {
    if (status !== 'pending') {
      return <Tag color="default">{t(`invitations:status.${status}`)}</Tag>;
    }
    
    if (new Date() > expiresAt) {
      return <Tag color="red" icon={<CloseCircleOutlined />}>{t('invitations:status.expired')}</Tag>;
    }
    
    return <Tag color="orange" icon={<ClockCircleOutlined />}>{t('invitations:status.pending')}</Tag>;
  };

  const getRoleLabel = (role: string) => {
    const roleLabels: { [key: string]: string } = {
      resident: t('invitations:roles.resident'),
      employee: t('invitations:roles.employee'),
      vendor: t('invitations:roles.vendor'),
      administrator: t('invitations:roles.administrator')
    };
    return roleLabels[role] || role;
  };

  const columns = [
    {
      title: t('invitations:fields.email'),
      dataIndex: 'userEmail',
      key: 'userEmail',
      render: (email: string) => <Text copyable>{email}</Text>,
    },
    {
      title: t('invitations:fields.role'),
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => <Tag color="blue">{getRoleLabel(role)}</Tag>,
    },
    {
      title: t('invitations:fields.invitedBy'),
      dataIndex: 'inviter',
      key: 'inviter',
      render: (inviter: any) => inviter?.displayName || '-',
    },
    {
      title: t('invitations:fields.invitedAt'),
      dataIndex: 'invitedAt',
      key: 'invitedAt',
      render: (date: Date) => dayjs(date).format('MMM DD, YYYY HH:mm'),
      sorter: (a: Invitation, b: Invitation) => dayjs(a.invitedAt).unix() - dayjs(b.invitedAt).unix(),
    },
    {
      title: t('invitations:fields.expiresAt'),
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: (date: Date) => dayjs(date).format('MMM DD, YYYY HH:mm'),
      sorter: (a: Invitation, b: Invitation) => dayjs(a.expiresAt).unix() - dayjs(b.expiresAt).unix(),
    },
    {
      title: t('invitations:fields.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: Invitation) => getStatusTag(status, record.expiresAt),
      filters: [
        { text: t('invitations:status.pending'), value: 'pending' },
        { text: t('invitations:status.accepted'), value: 'accepted' },
        { text: t('invitations:status.expired'), value: 'expired' },
        { text: t('invitations:status.cancelled'), value: 'cancelled' }
      ],
      onFilter: (value: boolean | React.Key, record: Invitation) => {
        if (value === 'expired') {
          return record.status === 'pending' && new Date() > record.expiresAt;
        }
        return record.status === value;
      }
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      render: (_: any, record: Invitation) => (
        <Space size="small">
          <Button 
            type="text" 
            icon={<EyeOutlined />} 
            size="small"
            onClick={() => window.open(`/invite?token=${record.id}`, '_blank')}
          >
            {t('invitations:actions.view')}
          </Button>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (invitations.length === 0) {
    return (
      <Card>
        <Empty 
          description={t('invitations:noInvitations')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  return (
    <Card>
      <div style={{ marginBottom: 16 }}>
        <Text strong>{t('invitations:title')}</Text>
        <Text type="secondary" style={{ marginLeft: 8 }}>
          {t('invitations:subtitle', { count: invitations.length })}
        </Text>
      </div>
      
      <Table
        columns={columns}
        dataSource={invitations}
        rowKey="id"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            t('common:showingRange', { from: range[0], to: range[1], total }),
        }}
        scroll={{ x: 800 }}
      />
    </Card>
  );
};

export default InvitationsTab;
